import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  clearAuthData,
  isJwtAuthFailure,
} from "../../components/content/axiosConfig";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import CustomModal from "../../components/custom/customModal";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast from "../../components/toast/toast";

const PondFishesViaPondDetail = () => {
    const { id } = useParams();
    const [imageToShow, setImageToShow] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [visitName, setVisitName] = useState("");

    const CheckExpiry = useCallback(async () => {
        await CheckUserExpiry();
    }, []);

    const fetchPondFishesPage = useCallback(async ({ search, page, limit }) => {
        try {
            return await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`, {
                params: { search, page, limit },
            });
        } catch (error) {
            if (isJwtAuthFailure(error)) {
                clearAuthData();
                window.location.href = "/user/login";
                showErrorToast("Session expired. Please log in again.");
                return { data: { data: [], total: 0 } };
            }
            showErrorToast(error.response?.data?.message || "Failed to fetch pond fishes");
            throw error;
        }
    }, [id]);

    const {
        rows: samples,
        totalCount,
        searchTerm,
        setSearchTerm,
        currentPage,
        setCurrentPage,
        isLoading,
        entriesPerPage,
    } = useServerPaginatedList(fetchPondFishesPage, { enabled: !!id });

    useEffect(() => {
        checkTokenValidity();
        CheckExpiry();
    }, [CheckExpiry]);

    useEffect(() => {
        if (!id) return;
        axiosInstance
            .get(`/api/PondDetail/GetPondDetailByID`, { params: { id } })
            .then((res) => setVisitName(res.data?.Name || ""))
            .catch(() => setVisitName(""));
    }, [id]);

    const showImage = (blobData) => {
        setModalVisible(true);
        const imageData = blobData.data;
        const base64Image = `data:image/jpeg;base64,${Buffer.from(imageData).toString("base64")}`;
        setImageToShow(base64Image);
    };

    if (isLoading && samples.length === 0) {
        return <Preloader />;
    }

    return (
        <div className="donezo-page donezo-workflow-page py-3">
            <div className="container-fluid px-3 px-md-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
                    <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
                        <PondDetailWorkflowPageHeader
                            pondDetailId={id}
                            section="pondFish"
                            detailName={visitName}
                            noBottomMargin
                        />
                    </div>
                </div>
                <PondDetailWorkflowTabs id={id} active="pondFish" />
                <div className="card border-0 shadow-sm donezo-workflow-card">
                    <div className="tab-content p-3 p-md-4">
                        <div className="input-group mb-3">
                            <div className="flex-fill position-relative">
                                <div className="input-group">
                                    <div
                                        className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                                        style={{ zIndex: 10 }}
                                    >
                                        <i className="fa fa-search opacity-5"></i>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control px-35px bg-light"
                                        placeholder="Search Sample..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-none d-md-block">
                            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Pond Name</th>
                                        <th>Pond Fish Type</th>
                                        <th>Number OF Fishes</th>
                                        <th>Image</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-5">
                                                Loading…
                                            </td>
                                        </tr>
                                    ) : (
                                        samples.map((web) => (
                                            <tr key={web.PondFishesID}>
                                                <td>{web.PondName}</td>
                                                <td>{web.FishTypeName}</td>
                                                <td>{web.NumberOfFishes}</td>
                                                <td>
                                                    {web.imagePath && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-info me-2"
                                                            onClick={() => showImage(web.imagePath)}
                                                        >
                                                            <i className="fa fa-eye"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-md-none">
                            {isLoading ? (
                                <div className="text-center text-muted py-4">Loading…</div>
                            ) : (
                                samples.map((web) => (
                                    <div key={web.PondFishesID} className="card mb-3 shadow-sm donezo-workflow-mobile-card">
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h5 className="card-title mb-0">{web.PondName}</h5>
                                                <div>
                                                    {web.imagePath ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-info me-2"
                                                            onClick={() => showImage(web.imagePath)}
                                                        >
                                                            <i className="fa fa-eye"></i>
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </div>
                                            <div className="row mt-2">
                                                <div className="col-6">
                                                    <p className="mb-1"><small className="text-muted">Fish Type:</small> {web.FishTypeName}</p>
                                                </div>
                                                <div className="col-6">
                                                    <p className="mb-1"><small className="text-muted">Count:</small> {web.NumberOfFishes}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <ListPagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            entriesPerPage={entriesPerPage}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>

                <CustomModal
                    isOpen={modalVisible}
                    onClose={() => setModalVisible(false)}
                    title="Fish Image"
                    showButton={true}
                    showCancelButton={true}
                    showSubmitButton={false}
                    modalSize="modal-lg"
                    minHeight="600px"
                >
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: "100%", height: "100%", minHeight: "400px" }}
                    >
                        <img
                            src={imageToShow}
                            alt="Fish"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                            }}
                        />
                    </div>
                </CustomModal>
            </div>
        </div>
    );
};

export default PondFishesViaPondDetail;
